import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { theme } from '../theme';
import ScrollReveal, { FloatingShape } from './ScrollReveal';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

const ContactSection = styled.section`
  padding: 6rem 2rem;
  background: ${theme.colors.neutral[50]};
  background-image: 
    radial-gradient(at 0% 0%, ${theme.colors.primary[50]} 0px, transparent 50%),
    radial-gradient(at 100% 100%, ${theme.colors.accent[50]} 0px, transparent 50%);
  scroll-margin-top: 80px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${theme.gradients.professional};
    opacity: 0.8;
  }

  @media (max-width: 768px) {
    padding: 4.5rem 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 3.5rem 1.25rem;
  }
`;

const Container = styled.div`
  max-width: 700px;
  margin: 0 auto;
  animation: ${fadeInUp} 0.6s ease-out;
  padding: 0 1rem;

  @media (max-width: 768px) {
    padding: 0 0.5rem;
  }

  @media (max-width: 480px) {
    padding: 0;
  }
`;

const Title = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  font-family: ${theme.typography.fontFamily.heading};
  font-weight: ${theme.typography.fontWeight.bold};
  text-align: center;
  margin-bottom: 1rem;
  color: ${theme.colors.neutral[900]};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -12px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: ${theme.gradients.professional};
    border-radius: ${theme.borderRadius.full};
  }
`;

const Subtitle = styled.p`
  text-align: center;
  color: ${theme.colors.neutral[600]};
  font-size: ${theme.typography.fontSize.lg};
  margin-bottom: 3rem;
  line-height: ${theme.typography.lineHeight.relaxed};
`;

const Form = styled.form`
  display: grid;
  gap: 2rem;
  background: white;
  padding: 3rem;
  border-radius: ${theme.borderRadius.xl};
  box-shadow: ${theme.shadows.lg};
  border: 1px solid ${theme.colors.neutral[200]};
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    gap: 1.75rem;
  }

  @media (max-width: 480px) {
    padding: 1.5rem 1.25rem;
  gap: 1.5rem;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  position: relative;
`;

const Label = styled.label`
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.neutral[700]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: color ${theme.transitions.base};
  
  &:has(+ input:focus),
  &:has(+ textarea:focus) {
    color: ${theme.colors.primary[600]};
  }
`;

const Input = styled.input`
  padding: 1rem 1.25rem;
  border: 2px solid ${theme.colors.neutral[200]};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.base};
  font-family: ${theme.typography.fontFamily.primary};
  color: ${theme.colors.neutral[900]};
  background: ${theme.colors.neutral[50]};
  transition: all ${theme.transitions.base};
  min-height: 44px;
  -webkit-appearance: none;
  touch-action: manipulation;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    background: white;
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}15;
    transform: translateY(-2px);
  }
  
  &:hover:not(:focus) {
    border-color: ${theme.colors.neutral[300]};
  }
  
  &::placeholder {
    color: ${theme.colors.neutral[400]};
  }

  @media (max-width: 480px) {
    padding: 0.875rem 1rem;
    font-size: 16px; /* Prevents zoom on iOS */
  }
`;

const TextArea = styled.textarea`
  padding: 1rem 1.25rem;
  border: 2px solid ${theme.colors.neutral[200]};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.base};
  font-family: ${theme.typography.fontFamily.primary};
  color: ${theme.colors.neutral[900]};
  background: ${theme.colors.neutral[50]};
  resize: vertical;
  min-height: 150px;
  transition: all ${theme.transitions.base};
  -webkit-appearance: none;
  touch-action: manipulation;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    background: white;
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}15;
    transform: translateY(-2px);
  }
  
  &:hover:not(:focus) {
    border-color: ${theme.colors.neutral[300]};
  }
  
  &::placeholder {
    color: ${theme.colors.neutral[400]};
  }

  @media (max-width: 480px) {
    padding: 0.875rem 1rem;
    font-size: 16px; /* Prevents zoom on iOS */
    min-height: 120px;
  }
`;

const SubmitButton = styled.button`
  padding: 1rem 2rem;
  background: ${props => props.disabled ? theme.colors.neutral[300] : theme.gradients.professional};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-family: ${theme.typography.fontFamily.primary};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all ${theme.transitions.base};
  box-shadow: ${props => props.disabled ? 'none' : theme.shadows.md};
  position: relative;
  overflow: hidden;
  min-height: 48px;
  touch-action: manipulation;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
    transform: translateX(-100%);
    transition: transform 0.6s;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.xl};
    
    &::before {
      transform: translateX(100%);
    }
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
  }

  @media (max-width: 480px) {
    width: 100%;
    padding: 0.875rem 1.5rem;
    font-size: ${theme.typography.fontSize.sm};
  }
`;

const Message = styled.div`
  padding: 1.25rem 1.5rem;
  margin-top: 1rem;
  border-radius: ${theme.borderRadius.lg};
  text-align: center;
  font-weight: ${theme.typography.fontWeight.medium};
  font-size: ${theme.typography.fontSize.base};
  animation: ${fadeInUp} 0.4s ease-out;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  
  ${props => props.type === 'success' ? `
    background-color: ${theme.colors.success}15;
    color: ${theme.colors.success};
    border: 2px solid ${theme.colors.success}30;
    
    &::before {
      content: '✓';
      font-size: 1.5rem;
      font-weight: bold;
    }
  ` : `
    background-color: ${theme.colors.error}15;
    color: ${theme.colors.error};
    border: 2px solid ${theme.colors.error}30;
    
    &::before {
      content: '✕';
      font-size: 1.5rem;
      font-weight: bold;
    }
  `}
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 0.8s linear infinite;
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // In production, use Netlify Functions endpoint
      // In development, use the local backend server
      const isProduction = import.meta.env.PROD;
      const apiUrl = isProduction ? '' : import.meta.env.VITE_API_URL || 'http://localhost:5000';
      const endpoint = isProduction ? '/.netlify/functions/send-email' : '/api/contact';
      
      console.log('API Configuration:', { isProduction, apiUrl, endpoint }); // Debug log

      const response = await fetch(`${apiUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        throw new Error('Invalid response from server');
      }

      if (response.ok) {
        setSubmitStatus({ 
          type: 'success', 
          message: 'Message sent successfully! I\'ll get back to you soon.' 
        });
        setFormData({ name: '', email: '', message: '' });
      } else {
        throw new Error(data.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setSubmitStatus({ 
        type: 'error', 
        message: error.message || 'Failed to send message. Please try again later.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContactSection id="contact">
      <FloatingShape $top="10%" $right="5%" $size="55px" $shape="circle" $gradient="linear-gradient(135deg, rgba(59,130,246,0.06), rgba(217,70,239,0.06))" $duration="15s" $delay="1s" />
      <FloatingShape $bottom="15%" $left="4%" $size="45px" $shape="rounded" $gradient="linear-gradient(135deg, rgba(16,185,129,0.06), rgba(245,158,11,0.06))" $duration="13s" $delay="3s" $rotation="25deg" />
      <Container>
        <ScrollReveal direction="spring" duration="1s">
          <Title>Get in Touch</Title>
          <Subtitle>
            Have a project in mind? Let's work together to bring your ideas to life.
          </Subtitle>
        </ScrollReveal>
        <ScrollReveal direction="flipX" delay="0.15s" duration="0.9s">
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="name">Full Name</Label>
            <Input
              type="text"
              id="name"
              name="name"
              placeholder="John Doe"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="email">Email Address</Label>
            <Input
              type="email"
              id="email"
              name="email"
              placeholder="john@example.com"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor="message">Your Message</Label>
            <TextArea
              id="message"
              name="message"
              placeholder="Tell me about your project..."
              value={formData.message}
              onChange={handleChange}
              required
            />
          </FormGroup>
          <SubmitButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <LoadingSpinner /> Sending...
              </>
            ) : (
              'Send Message'
            )}
          </SubmitButton>
          {submitStatus && (
            <Message type={submitStatus.type}>
              {submitStatus.message}
            </Message>
          )}
        </Form>
        </ScrollReveal>
      </Container>
    </ContactSection>
  );
}

export default Contact;
